import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import BackLink from "@/components/BackLink";
import PendingDraftBanner from "@/components/PendingDraftBanner";
import { Check, X, Edit3, RefreshCw, EyeOff, Mail, Phone, Image } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function modHeaders() {
  return { Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` };
}

const REJECT_REASONS = [
  "Otydligt anslag \u2013 behöver förtydligas",
  "Fel rubrik \u2013 passar inte in",
  "Olämpligt innehåll",
  "Dubblettanslag",
  "Annat",
];

export default function ModReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [anslag, setAnslag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // null, 'edit', 'reject', 'convert'
  const [editForm, setEditForm] = useState({});
  const [rejectForm, setRejectForm] = useState({ orsak: "", anteckning: "" });
  const [convertForm, setConvertForm] = useState({ titel: "", beskrivning: "", datum: "", tid: "", plats: "", arrangor: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!sessionStorage.getItem("mod_token")) { navigate("/mod/login"); return; }
    axios.get(`${API}/mod/anslag/${id}`, { headers: modHeaders() })
      .then(r => {
        setAnslag(r.data);
        setEditForm({ rubrik: r.data.rubrik, titel: r.data.titel, beskrivning: r.data.beskrivning, ort: r.data.ort || "", pristyp: r.data.pristyp || "", prisbelopp: r.data.prisbelopp || "" });
        setConvertForm({ titel: r.data.titel, beskrivning: r.data.beskrivning, datum: "", tid: "", plats: r.data.ort || "", arrangor: "" });
        setLoading(false);
      })
      .catch(() => { setLoading(false); });
  }, [id, navigate]);

  const action = async (fn) => {
    setBusy(true);
    try { await fn(); navigate("/mod"); } catch (err) { alert(err.response?.data?.detail || "Något gick fel"); }
    setBusy(false);
  };

  const publish = () => {
    if (anslag?.rubrik === "Evenemang") {
      const ok = window.confirm(
        "Detta publicerar endast anslaget. F\u00f6r att l\u00e4gga till i evenemangskalendern, anv\u00e4nd 'Omvandla till evenemang' eller 'Skapa evenemang'. Forts\u00e4tt?"
      );
      if (!ok) return;
    }
    return action(() => axios.post(`${API}/mod/anslag/${id}/publish`, {}, { headers: modHeaders() }));
  };
  const unpublish = () => action(() => axios.post(`${API}/mod/anslag/${id}/unpublish`, {}, { headers: modHeaders() }));
  const saveEdit = () => {
    if (editForm?.rubrik === "Evenemang") {
      const ok = window.confirm(
        "Detta publicerar endast anslaget. F\u00f6r att l\u00e4gga till i evenemangskalendern, anv\u00e4nd 'Omvandla till evenemang' eller 'Skapa evenemang'. Forts\u00e4tt?"
      );
      if (!ok) return;
    }
    return action(() => axios.put(`${API}/mod/anslag/${id}/edit`, editForm, { headers: modHeaders() }));
  };
  const reject = () => {
    if (!rejectForm.orsak) { alert("Välj en orsak"); return; }
    action(() => axios.post(`${API}/mod/anslag/${id}/reject`, rejectForm, { headers: modHeaders() }));
  };
  const convert = () => {
    if (!convertForm.datum || !convertForm.plats) { alert("Datum och plats krävs"); return; }
    action(() => axios.post(`${API}/mod/anslag/${id}/convert`, convertForm, { headers: modHeaders() }));
  };

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-14"><p className="text-muted-foreground">Laddar...</p></div>;
  if (!anslag) return <div className="max-w-3xl mx-auto px-4 py-14"><p className="text-muted-foreground">Anslaget hittades inte.</p></div>;

  const isIncoming = anslag.status === "Inskickat";
  const isPublished = anslag.status === "Publicerat";

  return (
    <div data-testid="mod-review-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <BackLink to="/mod" label="Moderatorvy" />
      <PendingDraftBanner urlPattern="/api/mod/anslag" />

      <div className="flex items-center gap-3 mb-4">
        <span className={`text-sm font-medium px-2.5 py-1 rounded-sm ${
          anslag.status === "Inskickat" ? "bg-amber-100 text-amber-800" :
          anslag.status === "Publicerat" ? "bg-green-100 text-green-800" :
          anslag.status === "Avvisat" ? "bg-red-100 text-red-800" :
          "bg-stone-100 text-stone-600"
        }`}>
          {anslag.status}
        </span>
        <span className="text-sm text-muted-foreground">{anslag.rubrik}</span>
      </div>

      <h1 className="font-heading text-2xl font-medium text-ink mb-4">{anslag.titel}</h1>

      <div className="bg-white border border-warm-border rounded-sm p-6 mb-6 space-y-4">
        <Row label="Beskrivning"><p className="whitespace-pre-line">{anslag.beskrivning}</p></Row>
        {anslag.pristyp && <Row label="Pristyp">{anslag.pristyp === "belopp" ? anslag.prisbelopp : anslag.pristyp}</Row>}
        {anslag.ort && <Row label="Ort">{anslag.ort}</Row>}
        {anslag.bild_url && <Row label="Bild"><img src={`${process.env.REACT_APP_BACKEND_URL}${anslag.bild_url}`} alt="Bifogad bild" className="max-w-xs rounded-sm border border-warm-border" /></Row>}
        <Row label="Avsändare">{anslag.avsandare_namn}</Row>
        <Row label="Kontakt">
          <div className="flex flex-col gap-1">
            {anslag.kontakt_epost && <span className="flex items-center gap-1"><Mail size={14} /> {anslag.kontakt_epost}</span>}
            {anslag.kontakt_telefon && <span className="flex items-center gap-1"><Phone size={14} /> {anslag.kontakt_telefon}</span>}
          </div>
        </Row>
        <Row label="Inskickat">{new Date(anslag.inskicksdatum).toLocaleString("sv-SE")}</Row>
        {anslag.samtycke && <Row label="Samtycke"><span className="text-pine">Godkänt</span></Row>}
        {anslag.avvisningsorsak && <Row label="Avvisningsorsak">{anslag.avvisningsorsak}</Row>}
        {anslag.avvisningsanteckning && <Row label="Anteckning">{anslag.avvisningsanteckning}</Row>}
      </div>

      {/* Action buttons */}
      {isIncoming && mode === null && (
        <>
          {anslag.rubrik === "Evenemang" && (
            <p data-testid="mod-evenemang-hint" className="text-sm text-ink bg-amber-50 border border-amber-200 rounded-sm p-3 mb-3">
              Detta anslag är markerat som <strong>Evenemang</strong>. &quot;Publicera anslag&quot; publicerar endast texten — använd <strong>&quot;Omvandla till evenemang&quot;</strong> för att lägga till i kalendern.
            </p>
          )}
          <div data-testid="mod-actions" className="flex flex-wrap gap-3 mb-6">
            {anslag.rubrik === "Evenemang" ? (
              <>
                <Btn icon={RefreshCw} label="Omvandla till evenemang" onClick={() => setMode("convert")} color="bg-pine hover:bg-pine-hover text-white" />
                <Btn icon={Check} label="Publicera anslag (utan kalender)" onClick={publish} disabled={busy} color="border-2 border-muted text-muted-foreground hover:bg-bone-alt" />
              </>
            ) : (
              <Btn icon={Check} label="Publicera" onClick={publish} disabled={busy} color="bg-pine hover:bg-pine-hover text-white" />
            )}
            <Btn icon={Edit3} label="Redigera" onClick={() => setMode("edit")} color="border-2 border-pine text-pine hover:bg-bone-alt" />
            <Btn icon={X} label="Avvisa" onClick={() => setMode("reject")} color="border-2 border-falu text-falu hover:bg-red-50" />
          </div>
        </>
      )}

      {isPublished && mode === null && (
        <div className="flex gap-3 mb-6">
          <Btn icon={EyeOff} label="Avpublicera" onClick={unpublish} disabled={busy} color="border-2 border-falu text-falu hover:bg-red-50" />
          <Btn icon={Edit3} label="Redigera" onClick={() => setMode("edit")} color="border-2 border-pine text-pine hover:bg-bone-alt" />
        </div>
      )}

      {/* Edit mode */}
      {mode === "edit" && (
        <div data-testid="mod-edit-form" className="bg-bone-alt rounded-sm p-6 mb-6 space-y-4">
          <h2 className="font-heading text-lg font-medium">Redigera anslag</h2>
          <Field label="Rubrik"><select value={editForm.rubrik} onChange={e => setEditForm(p => ({...p, rubrik: e.target.value}))} className="input-mod"><option>Hjälp i vardagen</option><option>Hand mot hand</option><option>Köp &amp; sälj lokalt</option><option>Evenemang</option></select></Field>
          <Field label="Titel"><input value={editForm.titel} onChange={e => setEditForm(p => ({...p, titel: e.target.value}))} className="input-mod" /></Field>
          <Field label="Beskrivning"><textarea value={editForm.beskrivning} onChange={e => setEditForm(p => ({...p, beskrivning: e.target.value}))} rows={4} className="input-mod" /></Field>
          <Field label="Ort"><input value={editForm.ort} onChange={e => setEditForm(p => ({...p, ort: e.target.value}))} className="input-mod" /></Field>
          <div className="flex gap-3">
            <button onClick={saveEdit} disabled={busy} className="bg-pine hover:bg-pine-hover text-white font-medium px-5 py-3 rounded-md text-base transition-colors">{busy ? "Sparar..." : "Spara och publicera"}</button>
            <button onClick={() => setMode(null)} className="text-muted-foreground hover:text-ink px-4 py-3 text-base">Avbryt</button>
          </div>
        </div>
      )}

      {/* Reject mode */}
      {mode === "reject" && (
        <div data-testid="mod-reject-form" className="bg-red-50 border border-red-200 rounded-sm p-6 mb-6 space-y-4">
          <h2 className="font-heading text-lg font-medium text-ink">Avvisa anslag</h2>
          <Field label="Orsak *">
            <select value={rejectForm.orsak} onChange={e => setRejectForm(p => ({...p, orsak: e.target.value}))} className="input-mod">
              <option value="">Välj orsak...</option>
              {REJECT_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Intern anteckning (valfritt)"><textarea value={rejectForm.anteckning} onChange={e => setRejectForm(p => ({...p, anteckning: e.target.value}))} rows={2} className="input-mod" /></Field>
          <div className="flex gap-3">
            <button onClick={reject} disabled={busy} className="bg-falu hover:bg-falu-hover text-white font-medium px-5 py-3 rounded-md text-base transition-colors">{busy ? "Avvisar..." : "Avvisa"}</button>
            <button onClick={() => setMode(null)} className="text-muted-foreground hover:text-ink px-4 py-3 text-base">Avbryt</button>
          </div>
        </div>
      )}

      {/* Convert mode */}
      {mode === "convert" && (
        <div data-testid="mod-convert-form" className="bg-bone-alt rounded-sm p-6 mb-6 space-y-4">
          <h2 className="font-heading text-lg font-medium text-ink">Omvandla till evenemang</h2>
          <Field label="Titel"><input value={convertForm.titel} onChange={e => setConvertForm(p => ({...p, titel: e.target.value}))} className="input-mod" /></Field>
          <Field label="Beskrivning"><textarea value={convertForm.beskrivning} onChange={e => setConvertForm(p => ({...p, beskrivning: e.target.value}))} rows={3} className="input-mod" /></Field>
          <Field label="Datum *"><input type="date" value={convertForm.datum} onChange={e => setConvertForm(p => ({...p, datum: e.target.value}))} className="input-mod" /></Field>
          <Field label="Tid"><input value={convertForm.tid} onChange={e => setConvertForm(p => ({...p, tid: e.target.value}))} placeholder="t.ex. 18:00" className="input-mod" /></Field>
          <Field label="Plats *"><input value={convertForm.plats} onChange={e => setConvertForm(p => ({...p, plats: e.target.value}))} className="input-mod" /></Field>
          <Field label="Arrangör"><input value={convertForm.arrangor} onChange={e => setConvertForm(p => ({...p, arrangor: e.target.value}))} className="input-mod" /></Field>
          <div className="flex gap-3">
            <button onClick={convert} disabled={busy} className="bg-pine hover:bg-pine-hover text-white font-medium px-5 py-3 rounded-md text-base transition-colors">{busy ? "Omvandlar..." : "Omvandla"}</button>
            <button onClick={() => setMode(null)} className="text-muted-foreground hover:text-ink px-4 py-3 text-base">Avbryt</button>
          </div>
        </div>
      )}

      {anslag.status === "Publicerat" && (
        <Link to={`/anslag/${anslag.id}`} className="text-base text-falu hover:text-falu-hover no-underline">Visa publik sida &rarr;</Link>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <p className="text-sm font-medium text-muted-foreground mb-1">{label}</p>
      <div className="text-base text-ink">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-ink mb-1">{label}</label>
      {children}
    </div>
  );
}

function Btn({ icon: Icon, label, onClick, disabled, color }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={`mod-btn-${label.toLowerCase().replace(/\s/g, "-")}`} className={`inline-flex items-center gap-2 font-medium px-4 py-3 rounded-md text-base transition-colors disabled:opacity-50 ${color}`}>
      <Icon size={18} /> {label}
    </button>
  );
}
